import React, { useEffect, useState, useContext, useRef } from 'react';
import { parseSrcset } from 'srcset';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useWindowSize } from "@react-hook/window-size";
import { useElementSize, useIntersectionObserver } from 'usehooks-ts'
import useMergedRef from '@react-hook/merged-ref';
import { appStateContext } from '../../context';
import { debounce } from '../../utils';
import { loadedImage, BackgroundImageProps } from '../../interfaces';
import { getDarkenedColor } from '../../helpers/color-helper';

// Verificamos si estamos en entorno de desarrollo
const isDevelopment = process.env.NODE_ENV === 'development';

export const BackgroundImage = (props: BackgroundImageProps) => {
  const { placeholder, className, srcset, defaultBackgroundImg, backgroundGradient, children, defaultWidth, innerContainerClasses, innerContainerStyle, setFrameColor, outerContainerStyle, frameDarken, hasFrame, deCorsify, ...rest } = props;

  const { loadedImages } = useContext(appStateContext);
  const hdImage = useRef<HTMLDivElement>(null);
  const [windowWidth, _windowHeight] = useWindowSize();
  const [elWidthRef, { width: elWidth, height: _elHeight }] = useElementSize<HTMLDivElement>();

  const [currentImage, setCurrentImage] = useState<loadedImage | undefined>(undefined);
  const [loadedImageWidth, setLoadedImageWidth] = useState(0);
  const [isFirstVisible, setIsFirstVisible] = useState(false);
  const [hadFrame, setHadFrame] = useState<boolean | undefined>(false);

  const entry = useIntersectionObserver(hdImage, {})
  const isVisible = !!entry?.isIntersecting

  // Determine the container proportion to use for calculating the appropriate image size
  // if the element hasn't been rendered yet, the proportion is 1
  const containerProportion = (elWidth ?? defaultWidth ?? windowWidth) / windowWidth;

  // Parse the srcset string into an array of SrcSetDefinition objects if srcset is provided
  const srcsetArray = srcset ? parseSrcset(srcset) : undefined;
  const sortedSrcsetArray = srcsetArray?.sort((a, b) => (a.width ?? 0) - (b.width ?? 0));

  // among all the images in the array, get the smallest image that is larger than the window width. if none are larger, return the last image in the array
  const getMatchingImage = () => {
    const filteredArray = sortedSrcsetArray?.filter(image => image.width && image.width > (windowWidth * containerProportion) + 0);
    return filteredArray?.[0] || sortedSrcsetArray?.[sortedSrcsetArray.length - 1];
  };

  const getMatchingImageFromLoadedImages = (src: string) =>
    loadedImages?.find(img => img.url === src) ?? undefined;

  const addImageToLoadedImages = (imgData: loadedImage) => {
    if (!getMatchingImageFromLoadedImages(imgData.url)) {
      loadedImages.push({
        url: imgData.url,
        width: imgData.width,
        height: imgData.height,
        average_color: imgData.average_color,
        is_inline: imgData.is_inline || false,
      });
    }
  }

  // dont modify is_inline nor the src, which acts as the key
  // modify the original object in the loadedImages array
  const modifyLoadedImage = (imgData: loadedImage) => {
    const image = getMatchingImageFromLoadedImages(imgData.url);
    if (image) {
      const index = loadedImages.indexOf(image);
      loadedImages.splice(index, 1, imgData);
    }
  }

  const getImgToLoad = () => {
    const matchingImage = getMatchingImage();
    const _imgToLoad = matchingImage || defaultBackgroundImg;
    return {
      url: _imgToLoad.url,
      width: 'width' in _imgToLoad ? _imgToLoad.width : undefined,
    } as loadedImage;
  }

  const doLoad = () => {
    const imgToLoad = getImgToLoad();
    setCurrentImage(imgToLoad);
    addImageToLoadedImages(imgToLoad);
    setLoadedImageWidth(imgToLoad.width ?? 0);
  }

  useEffect(() => {
    // i prefer to trigger the load when the image is first visible instead of if it isn't loaded because it fires exactly once
    if (isVisible && !isFirstVisible) {
      setIsFirstVisible(true);
      doLoad();
    }
  }, [isVisible, isFirstVisible]);

  const deCorsUrl = (url: string) => {
    // Devolver la URL sin modificar
    return url;
  }

  const _setFrameColor = (color: string | undefined) => {
    if (setFrameColor) {
      setFrameColor(color);
    }
  }

  const handleHasFrameChange = () => {
    if (hasFrame) {
      const isHexColor = placeholder?.startsWith('#');

      if (isHexColor) {
        const finalColor = frameDarken ? getDarkenedColor(placeholder!, frameDarken) : placeholder;
        _setFrameColor(finalColor);
        setHadFrame(hasFrame);
        return;
      }

      setHadFrame(hasFrame);
    }
    // was previously loaded, but now has no frame, is missing gradient
    if (!hasFrame && hadFrame) {
      doLoad();
    }
  }

  // Update the background image when the window width changes
  const handleResize = () => {
    if (sortedSrcsetArray?.length) {
      const matchingImage = getMatchingImage();

      // Only update the style property of the current element with the new image url if the new image is larger than the one currently loaded
      if (matchingImage?.width && (matchingImage?.width > loadedImageWidth)) {
        doLoad();
      }
    }
  };

  const debouncedHandleResize = debounce(handleResize, 500);

  useEffect(() => {
    handleHasFrameChange();
  }, [hasFrame]);

  useEffect(() => {
    window.addEventListener("resize", debouncedHandleResize);
    return () => window.removeEventListener("resize", debouncedHandleResize);
  }, [windowWidth, containerProportion, elWidth, loadedImageWidth]);

  const isHexColor = placeholder?.startsWith('#');
  const placeholderValue = isHexColor ? `linear-gradient(0deg, ${placeholder} 0%, ${placeholder} 100%)` : `url('${placeholder}')`;

  const backgroundImages = [
    backgroundGradient,
    currentImage?.url && `url('${currentImage.url}')`,
    placeholder && placeholderValue,
  ].filter(Boolean).join(', ') || undefined;

  const backgroundSizes = [
    backgroundGradient && "auto",
    currentImage?.url && `cover`,
    placeholder && (isHexColor ? "100% 100%" : "auto"),
  ].filter(Boolean).join(', ') || undefined;

  const styles = {
    ...outerContainerStyle,
    backgroundImage: backgroundImages,
    backgroundSize: backgroundSizes,
    backgroundColor: isHexColor ? placeholder : undefined,
  };

  return (
    <div
      ref={useMergedRef(elWidthRef, hdImage)}
      style={styles}
      className={[
        `bg-center bg-cover w-full`,
        className || '',
      ].join(' ')}
      {...rest}
    >
      <div
        style={innerContainerStyle}
        className={[
          `flex flex-col items-center`,
          innerContainerClasses ?? '',
        ].join(' ')}>
        {children}
      </div>
    </div>
  );
};
